import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  FlaskConical, Droplets, Layers, Filter, GitBranch, Waves, Gauge, Cpu,
  CircleDot, SprayCan, Wrench, Plus, BookOpen, Search, CheckCircle2,
  Clock, Loader2, ChevronRight, Users
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import AskLibraryBar from "../components/engineering/AskLibraryBar";

const ICON_MAP = {
  Droplets, Layers, Filter, GitBranch, FlaskConical, Waves,
  Gauge, Cpu, CircleDot, SprayCan, Wrench,
};

function CategoryIcon({ name, className }) {
  const Icon = ICON_MAP[name] || Wrench;
  return <Icon className={className} />;
}

function SessionCard({ s, cats }) {
  const cat = cats.find((c) => c.key === s.category_key);
  const isShared = s._view_role === "shared";
  return (
    <Link
      to={`/tools/engineering-desk/${s.id}`}
      className="hv-card p-3 hover:border-[#1FB6C1]/40 transition block"
      data-testid={`eng-session-card-${s.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-[#1FB6C1]/15 text-[#1FB6C1] flex items-center justify-center shrink-0">
          <CategoryIcon name={cat?.icon || "Wrench"} className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-bold text-sm truncate">{s.title || "Untitled"}</div>
            {s.status === "resolved" && (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            )}
            {isShared && (
              <span className="text-[8px] uppercase tracking-[0.18em] px-1 py-0.5 rounded bg-violet-500/10 text-violet-300 border border-violet-500/30 shrink-0">
                Shared
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-[#a0aabf] mt-0.5">
            <span>{cat?.label || "General"}</span>
            <span className="opacity-40">·</span>
            <span>{s.message_count || 0} msgs</span>
            {isShared && s.owner_name && (
              <>
                <span className="opacity-40">·</span>
                <span>by {s.owner_name}</span>
              </>
            )}
            {s.link_label && (
              <>
                <span className="opacity-40">·</span>
                <span className="truncate">→ {s.link_label}</span>
              </>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#647087] shrink-0 mt-1" />
      </div>
    </Link>
  );
}

function LibraryCard({ s }) {
  const sp = s.summary_public || {};
  // Wave 26.10.5 — exports + sessions both now route to the dedicated
  // Library Detail page (which shows the report only, never the chat).
  // Old: sessions linked to the chat page which 403'd for non-owners.
  const to = `/tools/engineering-desk/library/${s.kind || "session"}/${s.id}`;
  const isExport = s.kind === "export";
  const unread = !s.read;
  return (
    <Link
      to={to}
      className={`hv-card p-3 transition block ${
        unread
          ? "border-[#1FB6C1]/40 hover:border-[#1FB6C1]/70 bg-[#1FB6C1]/[0.03]"
          : "hover:border-[#1FB6C1]/40"
      }`}
      data-testid={`eng-library-card-${s.id}`}
    >
      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1] font-bold">
          {s.category_key?.replaceAll("_", " ")}
        </div>
        {isExport && (
          <span
            className="text-[9px] uppercase tracking-[0.15em] text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded px-1.5 py-0.5"
            title={`Custom report · ${sp.audience || "?"} · ${sp.length || "?"}`}
            data-testid={`eng-library-export-chip-${s.id}`}
          >
            Report
          </span>
        )}
        {unread && (
          <span
            className="ml-auto inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] text-[#1FB6C1] font-bold"
            data-testid={`eng-library-unread-${s.id}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#1FB6C1] animate-pulse" />
            Unread
          </span>
        )}
      </div>
      <div className={`font-display text-sm leading-snug ${unread ? "font-bold text-white" : "font-medium text-[#cbd2df]"}`}>
        {sp.headline || s.title}
      </div>
      {sp.tldr && <div className="text-xs text-[#a0aabf] mt-1 line-clamp-2">{sp.tldr}</div>}
      <div className="flex items-center gap-2 text-[11px] text-[#a0aabf] mt-2 pt-2 border-t border-white/5">
        <span className="w-5 h-5 rounded-full bg-[#1FB6C1]/15 border border-[#1FB6C1]/30 flex items-center justify-center text-[9px] font-bold text-[#1FB6C1] shrink-0">
          {(s.owner_name || "?").slice(0, 1).toUpperCase()}
        </span>
        <span className="font-medium text-[#cbd2df] truncate" data-testid={`eng-library-author-${s.id}`}>
          {s.owner_name || "Unknown"}
        </span>
        {s.resolved_at && (
          <span className="text-[#647087] tabular-nums">
            · {new Date(s.resolved_at).toLocaleDateString("en-GB")}
          </span>
        )}
      </div>
    </Link>
  );
}

export default function EngineeringDesk() {
  const [tab, setTab] = useState("mine"); // mine · shared · library
  const [sessions, setSessions] = useState([]);
  const [shared, setShared] = useState([]);
  const [library, setLibrary] = useState([]);
  const [libraryUnread, setLibraryUnread] = useState(0);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [c, sess, sh] = await Promise.all([
          api.get("/engineering/categories"),
          api.get("/engineering/sessions?scope=mine"),
          api.get("/engineering/sessions?scope=shared"),
        ]);
        setCats(c.data.items || []);
        setSessions(sess.data.items || []);
        setShared(sh.data.items || []);
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not load."); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (tab !== "library") return;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (categoryFilter) params.set("category", categoryFilter);
        if (search) params.set("q", search);
        const { data } = await api.get(`/engineering/library?${params}`);
        setLibrary(data.items || []);
        setLibraryUnread(data.unread_count || 0);
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not load library."); }
    })();
  }, [tab, categoryFilter, search]);

  // Lightweight prefetch of unread count on first mount so the tab
  // badge can show "Library (3 new)" before the user clicks the tab.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/engineering/library");
        setLibraryUnread(data.unread_count || 0);
      } catch (_e) { /* fail-soft */ }
    })();
  }, []);

  const createSession = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/engineering/sessions", {
        title: "New session",
        category_key: categoryFilter || "general",
      });
      nav(`/tools/engineering-desk/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create session.");
    } finally {
      setCreating(false);
    }
  };

  const visible = (tab === "shared" ? shared : sessions).filter(
    (s) => (!categoryFilter || s.category_key === categoryFilter) &&
           (!search || (s.title || "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-5" data-testid="engineering-desk">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Engineering <span className="text-[#1FB6C1]">Desk</span>
          </h1>
          <p className="text-sm text-[#a0aabf] mt-1 max-w-xl">
            Deep-reasoning sessions with Hidro for fault-finding, research and
            site problem-solving. Your chats stay private — resolved write-ups
            become a shared library for the team.
          </p>
        </div>
        <button
          onClick={createSession}
          disabled={creating}
          className="hv-btn hv-btn-primary text-sm"
          data-testid="eng-new-session"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          New session
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/10 overflow-x-auto">
        {[
          { k: "mine", label: "My sessions", Icon: Clock, count: sessions.length, badge: null },
          { k: "shared", label: "Shared with me", Icon: Users, count: shared.length, badge: null },
          { k: "library", label: "Library", Icon: BookOpen, count: library.length, badge: libraryUnread },
        ].map(({ k, label, Icon, count, badge }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] border-b-2 transition flex items-center gap-1.5 shrink-0 ${tab === k ? "border-[#1FB6C1] text-[#1FB6C1]" : "border-transparent text-[#a0aabf] hover:text-white"}`}
            data-testid={`eng-tab-${k}`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
            {count > 0 && <span className="text-[9px] opacity-70">({count})</span>}
            {badge > 0 && (
              <span
                className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#1FB6C1] text-[#0b1320]"
                data-testid={`eng-tab-${k}-unread`}
              >
                {badge} new
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#647087]" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === "library" ? "Search library…" : "Search my sessions…"}
            className="hv-input text-sm pl-7 w-full"
            data-testid="eng-search"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="hv-input text-sm"
          data-testid="eng-category-filter"
        >
          <option value="">All categories</option>
          {cats.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-[#647087]">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : tab === "library" ? (
        <div className="space-y-4">
          <AskLibraryBar />
          <div className="grid md:grid-cols-2 gap-3">
            {library.length === 0 ? (
              <div className="hv-card p-6 text-center text-sm text-[#a0aabf] md:col-span-2">
                The library is empty — it fills up as your team resolves sessions.
              </div>
            ) : (
              library.map((s) => <LibraryCard key={s.id} s={s} />)
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.length === 0 ? (
            <div className="hv-card p-6 text-center text-sm text-[#a0aabf]">
              {tab === "shared"
                ? "No sessions shared with you yet."
                : sessions.length === 0
                  ? "No sessions yet. Hit \"New session\" to ask Hidro your first engineering question."
                  : "No sessions match the current filter."}
            </div>
          ) : (
            visible.map((s) => <SessionCard key={s.id} s={s} cats={cats} />)
          )}
        </div>
      )}
    </div>
  );
}
